import React, { useState } from 'react';
import { useFormatters, useTextMasks, useValidators } from '../../../hooks';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Divider,
  Typography,
  Tooltip,
} from '../../../ui/core';
import { useDispatch } from 'react-redux';
import { updateTripPayout } from '../../../redux/Trips/actions';
import { useHasPermission } from '../../../helpers/api/auth/index';
const TripPayoutField = ({ trip }) => {
  const f = useFormatters();
  const m = useTextMasks();
  const v = useValidators();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { autoBonus, base, premier, manualBonus, extendedBonus, waitTime, emptyReturn, returnCancellationDeduction, extraStopsBonus, delayPenalty, ghostLegOutbound, ghostTripBonus } = trip.payoutItemized || {};
  const initialValue = f.number.currency(manualBonus || 0);

  const [value, setValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const isValid = v.text.currency()(value) === true;
  const hasChanges = value !== initialValue;

  const hasManualBonusPermission = useHasPermission('manual_bonus');

  // Calculate one-way total dynamically
  const calculateOneWayTotal = () => {
    const manualBonusValue = Number.parseFloat(value.replace('$', '')) || 0;
    return (base || 0) +
           (premier || 0) +
           (waitTime || 0) +
           (autoBonus || 0) +
           (extendedBonus || 0) +
           (returnCancellationDeduction || 0) +
           (extraStopsBonus || 0) +
           manualBonusValue;
  };

  const onSave = async () => {
    setIsSaving(true);
    const update = {
      manualBonus: Number.parseFloat(value.replace('$', '')),
    };
    const { success } = await updateTripPayout(trip.id, update)(dispatch);
    setIsSaving(false);
    if (success) {
      setOpen(false);
    }
  };

  const PayoutRow = ({ label, value, style }) => (
    <Stack direction="row" spacing="20px"  sx={{ pt: '8px', ...style }}>
      <Typography sx={{ width: '80%', textAlign: 'left' }}>
        {label}
      </Typography>
      <Typography sx={{ width: '20%', textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );

  return (
    <>
      <Button onClick={() => setOpen(true)}>{f.number.currency(trip.payout)}</Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Edit Payout</DialogTitle>
        <Divider />
        <DialogContent sx={{ minWidth: '400px' }}>
          <PayoutRow label="Base" value={f.number.currency(base)} />
          <PayoutRow label="Premier" value={f.number.currency(premier)} />
          <PayoutRow label="Wait Time" value={f.number.currency(waitTime || 0)} />
          <PayoutRow label="Auto Bonus" value={f.number.currency(autoBonus)} />
          <PayoutRow label="Extended Bonus" value={f.number.currency(extendedBonus || 0)} />
          <PayoutRow label="Extra Stops" value={f.number.currency(extraStopsBonus || 0)} />
          <PayoutRow label="Delay Penalty" value={f.number.currency(delayPenalty || 0)} />
          <PayoutRow label="Return Cancellation" value={f.number.currency(returnCancellationDeduction || 0)} />
          <Stack direction="row" spacing="20px"  sx={{ pt: '4px', alignItems: 'center' }}>
            <Typography sx={{ width: '80%', textAlign: 'left' }}>
              Manual Bonus
              <Tooltip
                open={tooltipOpen}
                onClose={() => setTooltipOpen(false)}
                title={
                  <Stack spacing={1}>
                    {[['40%', 24], ['80%', 48], ['110%', 66], ['140%', 84]].map(([label, val]) => (
                      <Typography key={label} variant="body2">
                        {label} = {f.number.currency(val)}
                      </Typography>
                    ))}
                  </Stack>
                }
              >
                <span
                  style={{ marginLeft: '6px', cursor: 'pointer' }}
                  onClick={() => setTooltipOpen(!tooltipOpen)}
                >
                  ⚡︎
                </span>
              </Tooltip>
            </Typography>
            <Typography sx={{ width: '20%', textAlign: 'right' }}>
              <TextField
                value={value}
                mask={m.text.currency}
                disabled={!hasManualBonusPermission}
                onChange={(e) => setValue(e.target.value)}
                inputProps={{
                  style: { textAlign: 'right', padding: '6px', width: '70px', marginLeft: '-6px' },
                }}
              />
            </Typography>
          </Stack>

          <Divider sx={{ mt: '16px', mb: '10px' }} />
          <PayoutRow
            label="One-way total"
            value={f.number.currency(calculateOneWayTotal())}
            style={{ marginTop: '0px', '& > *': { fontWeight: 'bold' } }}
          />

          <PayoutRow
            label="+ Empty Return"
            value={f.number.currency(emptyReturn)}
          />
          {ghostLegOutbound ? (
            <PayoutRow label="+ Ghost Leg Outbound" value={f.number.currency(ghostLegOutbound)} />
          ) : null}
          {ghostTripBonus ? (
            <PayoutRow label="+ Ghost Trip Bonus" value={f.number.currency(ghostTripBonus)} />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          {hasManualBonusPermission && <Button disabled={!(isValid && hasChanges)} loading={isSaving} onClick={onSave}>
            Update Payout
          </Button>}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TripPayoutField;
